/**
 * Identity linking for the D-044 layer.
 *
 * Every call is explicit: importing this module never opens a wallet or asks
 * for a signature. Callers link at the moment of value (a claim, a drop, an
 * `invoked -> connected` step), never at boot.
 */
import { getToken } from './authService';
import * as core from '../../services/coreClient';

function authToken(token) {
  const value = token || getToken();
  if (!value) throw new Error('A core session is required to link an identity');
  return value;
}

export function hasLinkedWallet(identities = []) {
  return (Array.isArray(identities) ? identities : []).some(
    (row) => row?.provider === 'wallet' && row?.status === 'verified',
  );
}

export function linkedProviders(identities = []) {
  return (Array.isArray(identities) ? identities : [])
    .filter((row) => row?.status === 'verified')
    .map((row) => row.provider);
}

async function signMessage(signer, message, address) {
  if (typeof signer === 'function') return signer(message, address);
  if (signer && typeof signer.signMessage === 'function') {
    return signer.signMessage(message);
  }
  if (typeof signer === 'string') return signer;
  throw new Error('A wallet signer is required');
}

export async function linkWallet({ provider = 'base', address, signer, token } = {}) {
  if (!address) throw new Error('A wallet address is required');
  const nonce = await core.getWalletNonce(address);
  if (!nonce?.message) throw new Error('The server did not issue a wallet message');
  const signature = await signMessage(signer, nonce.message, address);
  return core.linkIdentity(authToken(token), {
    provider,
    address: String(address).toLowerCase(),
    signature,
  });
}

/**
 * Link the wallet the player picks from the canonical provider ladder.
 *
 * This is the one-click flow shared by Evidence and Coat. It stays explicit:
 * the browser provider is only asked for accounts and a signature after the
 * player clicks, and the server still issues the nonce and verifies the proof.
 */
export async function linkCurrentWallet({ provider = 'base', token } = {}) {
  const { getProvider } = await import('./getProvider');
  const wallet = await getProvider();
  const accounts = await wallet.request({ method: 'eth_requestAccounts' });
  const address = Array.isArray(accounts) ? accounts[0] : null;
  if (!address) {
    const error = new Error('No wallet selected');
    error.code = 'WALLET_NOT_SELECTED';
    throw error;
  }
  return linkWallet({
    provider,
    address,
    signer: (message, signerAddress) =>
      wallet.request({ method: 'personal_sign', params: [message, signerAddress] }),
    token,
  });
}

export function walletLinkErrorMessage(error) {
  if (!error) return 'LINK FAILED. TRY AGAIN.';
  if (error.code === 'WALLET_NOT_SELECTED') return 'NO WALLET SELECTED. LINK CANCELLED.';
  if (error.code === 4001 || /reject|cancel|denied/i.test(String(error.message || ''))) {
    return 'LINK CANCELLED. WALLET STAYS UNLINKED.';
  }
  return 'LINK FAILED. SIGNATURE NOT ACCEPTED. TRY AGAIN.';
}

export async function linkFarcaster({ jwt, token } = {}) {
  if (!jwt) throw new Error('A Farcaster Quick Auth token is required');
  return core.linkIdentity(authToken(token), { provider: 'farcaster', jwt });
}

export async function linkWorldWallet({ address, signer, token } = {}) {
  return linkWallet({ provider: 'world', address, signer, token });
}

export async function linkWorldId({ idkit, token } = {}) {
  if (!idkit) throw new Error('A World ID result is required');
  return core.linkIdentity(authToken(token), { provider: 'world_id', idkit });
}

export async function loadIdentity(token) {
  return core.listIdentities(authToken(token));
}

export default {
  hasLinkedWallet,
  linkedProviders,
  linkCurrentWallet,
  linkFarcaster,
  linkWallet,
  linkWorldId,
  linkWorldWallet,
  loadIdentity,
  walletLinkErrorMessage,
};
